import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getActiveSales } from '../services/api';
import { useAllCategories, useAllBrands } from '../hooks/useAllProductsQueries';
import { ChevronRight } from 'lucide-react';
import ProductCard from '../components/ui/ProductCard';

const SORT_OPTIONS = [
  { value: 'saleEndDate-asc', label: 'Ending Soon', sortBy: 'saleEndDate', sortOrder: 'asc' },
  { value: 'discountPercentage-desc', label: 'Highest Discount', sortBy: 'discountPercentage', sortOrder: 'desc' },
  { value: 'newest-desc', label: 'Newest', sortBy: 'newest', sortOrder: 'desc' },
];

const SalesPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [filters, setFilters] = useState({
    category: '',
    brand: '',
    sortBy: 'saleEndDate',
    sortOrder: 'asc',
    page: 1,
    limit: 12
  });

  const { data: categories } = useAllCategories();
  const { data: brands } = useAllBrands();

  // Fetch sales products
  useEffect(() => {
    fetchSalesProducts();
  }, [filters]);

  const fetchSalesProducts = async () => {
    try {
      setLoading(true);
      const response = await getActiveSales(filters);
      const allProducts = response.data.products || [];

      // Filter out expired sales on client side as additional safety
      const now = new Date();
      const validProducts = allProducts.filter(product => {
        if (!product.isSaleActive || !product.saleEndDate) return false;
        const endDate = new Date(product.saleEndDate);
        return endDate > now; // Only include sales that haven't ended yet
      });

      setProducts(validProducts);
      setPagination(response.data.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        hasNextPage: false,
        hasPrevPage: false
      });
    } catch (error) {
      console.error('Error fetching sales products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value,
      page: 1 // Reset to page 1 when filters change
    }));
  };

  const handleSortChange = (value) => {
    const option = SORT_OPTIONS.find(o => o.value === value);
    if (!option) return;
    setFilters(prev => ({
      ...prev,
      sortBy: option.sortBy,
      sortOrder: option.sortOrder,
      page: 1
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
    window.scrollTo(0, 0);
  };

  const currentSortValue = `${filters.sortBy}-${filters.sortOrder}`;
  const selectClass = "px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white";

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-sm md:text-base text-gray-700">Loading sales...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 pt-4">
        <nav className="flex items-center gap-1.5 text-xs text-gray-500">
          <Link to="/" className="hover:text-gray-700 transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-secondary-600 font-medium">Sales</span>
        </nav>
      </div>

      <div className="container mx-auto px-4 pt-3 pb-8">
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-heading font-bold text-gray-900">Sales</h1>
          <p className="text-sm text-gray-500 mt-1">Discounted products, updated as offers go live.</p>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            className={selectClass}
          >
            <option value="">All Categories</option>
            {categories?.map((category) => (
              <option key={category._id} value={category._id}>
                {category.name}
              </option>
            ))}
          </select>

          <select
            value={filters.brand}
            onChange={(e) => handleFilterChange('brand', e.target.value)}
            className={selectClass}
          >
            <option value="">All Brands</option>
            {brands?.map((brand) => (
              <option key={brand._id} value={brand._id}>
                {brand.name}
              </option>
            ))}
          </select>

          <select
            value={currentSortValue}
            onChange={(e) => handleSortChange(e.target.value)}
            className={selectClass + " sm:ml-auto"}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Products Grid */}
        <div>
          {products.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2 font-heading">No sales found</h3>
              <p className="text-sm md:text-base text-gray-500">There are currently no active sales. Check back later!</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 md:gap-6">
                {products.map((product) => (
                  <ProductCard key={product._id} product={product} />
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="mt-8 flex justify-center">
                  <nav className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={!pagination.hasPrevPage}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-300"
                    >
                      Previous
                    </button>

                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 text-sm font-medium rounded-md transition duration-300 ${
                          page === pagination.currentPage
                            ? 'bg-primary-500 text-white hover:bg-primary-600'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-300"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesPage;
